import {errorToastOptions} from "../ErrorToast";
import {
    IonButton, IonButtons,
    IonContent, IonHeader, IonIcon,
    IonItem,
    IonItemDivider,
    IonLabel, IonList, IonModal, IonNote,
    IonSearchbar, IonSelect, IonSelectOption, IonTitle, IonToolbar, useIonAlert,
    useIonToast
} from "@ionic/react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {add} from "ionicons/icons";
import {Autocomplete} from "./Autocomplete";
import {
    accessLevels,
    createAccess2Access,
    deleteAccess2Access,
    getAccess2Accesses,
    getAccessLevelTitle,
    getSearchableServices,
    getSearchableUsers,
    setAccess2AccessLevel
} from "../../repositories/adminAccess2AccessRepository";

const isValidId = (item) => item !== undefined && Number.isInteger(+item.id) && +item.id > 0;

export const AdminManageUserAccess2Accesses = ({user}) => {
    const [entries, setEntries] = useState([]);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [editedAccessLevel, setEditedAccessLevel] = useState(null);
    const [newEntry, setNewEntry] = useState({});
    const [search, setSearch] = useState('');
    const [presentToast] = useIonToast();
    const [presentAlert] = useIonAlert();
    const modal = useRef();

    const presentError = useCallback(async (e, fallbackMessage) => {
        const message = typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallbackMessage;
        await presentToast(errorToastOptions(message));
    }, [presentToast]);

    const reloadEntries = useCallback(async () => {
        try {
            setEntries(await getAccess2Accesses(user.id));
        } catch (e) {
            await presentError(e, "Failed to load managed accesses");
        }
    }, [user.id, presentError]);

    useEffect(() => {
        reloadEntries();
    }, [reloadEntries]);

    const openEditModal = (entry) => () => {
        setSelectedEntry(entry);
        setEditedAccessLevel(entry.accessLevel);
    }

    const saveAccessLevel = async () => {
        try {
            await setAccess2AccessLevel(selectedEntry, editedAccessLevel);
        } catch (e) {
            await presentError(e, "Failed to save access level");
            return;
        }
        setSelectedEntry(null);
        await presentToast({message: "Successfully saved access level", duration: 2000, color: "success"});
        await reloadEntries();
    }

    const removeEntry = () => {
        presentAlert({
            header: 'Confirmation',
            message: `Are you sure ${user.name} should no longer manage this access?`,
            buttons: [
                {text: 'Cancel', role: 'cancel'},
                {
                    text: 'Remove',
                    role: 'destructive',
                    handler: async () => {
                        try {
                            await deleteAccess2Access(selectedEntry);
                        } catch (e) {
                            await presentError(e, "Failed to remove managed access");
                            return;
                        }
                        setSelectedEntry(null);
                        await presentToast({message: "Successfully removed managed access", duration: 2000, color: "success"});
                        await reloadEntries();
                    }
                }
            ]
        })
    }

    async function confirm() {
        try {
            await createAccess2Access({
                managerUserId: user.id,
                userId: +newEntry.user.id,
                serviceId: +newEntry.service.id,
                accessLevel: newEntry.accessLevel
            });
        } catch (e) {
            await presentError(e, "Failed to add managed access");
            return;
        }
        modal.current?.dismiss(newEntry, 'confirm');
        await presentToast({message: "Successfully added managed access", duration: 2000, color: "success"});
        await reloadEntries();
    }

    function cancel() {
        presentAlert({
            header: 'Confirmation',
            message: 'Are you sure you want to discard the changes?',
            buttons: [
                {text: 'Cancel', role: 'cancel'},
                {text: 'Confirm', handler: () => modal.current?.dismiss()}
            ]
        })
    }

    const describeEntry = (entry) => ({
        user: entry.userId === null ? 'Any user' : (entry.userName ?? `Unknown user ${entry.userId}`),
        service: entry.serviceId === null
            ? 'Any service'
            : [
                entry.serviceName ?? `Unknown service ${entry.serviceId}`,
                entry.serviceAdditionalInformation,
                entry.serviceTypeName && `(${entry.serviceTypeName})`
            ].filter(Boolean).join(' ')
    });

    const normalizedSearch = search.trim().toLowerCase();
    const filteredEntries = normalizedSearch
        ? entries.filter(entry => {
            const description = describeEntry(entry);
            return [description.user, description.service, getAccessLevelTitle(entry.accessLevel)]
                .some(value => value.toLowerCase().includes(normalizedSearch));
        })
        : entries;

    return (
        <div>
            <IonItemDivider sticky className="usernames-list-search-bar">
                <IonButton className="ion-margin-start" fill="outline" slot="start" id="open-access2access-modal">
                    <IonIcon icon={add} />
                </IonButton>
                <IonSearchbar
                    placeholder="Search by user, service or access level"
                    debounce={300}
                    mode="ios"
                    enterkeyhint="search" inputmode="search" onIonInput={(ev) => setSearch(ev.detail.value ?? '')}></IonSearchbar>
            </IonItemDivider>
            <IonList>
                {filteredEntries.length === 0 && (
                    <IonItem lines="none">
                        <IonLabel color="medium">
                            {entries.length === 0 ? `${user.name} doesn't manage any accesses` : 'No accesses match the search'}
                        </IonLabel>
                    </IonItem>
                )}
                {filteredEntries.map((entry, index) => {
                    const description = describeEntry(entry);
                    return <IonItem lines={index === filteredEntries.length - 1 ? "none" : "inset"}
                                    key={`${entry.userId}-${entry.serviceId}`}
                                    button onClick={openEditModal(entry)}>
                        <IonLabel>
                            <h3>{ description.user }</h3>
                            <p>{ description.service }</p>
                        </IonLabel>
                        <IonNote slot="end">{ getAccessLevelTitle(entry.accessLevel) }</IonNote>
                    </IonItem>
                })}
            </IonList>

            <IonModal isOpen={selectedEntry !== null} onDidDismiss={() => setSelectedEntry(null)}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Edit managed access</IonTitle>
                        <IonButtons slot="start">
                            <IonButton onClick={() => setSelectedEntry(null)}>Cancel</IonButton>
                        </IonButtons>
                        <IonButtons slot="end">
                            <IonButton
                                disabled={editedAccessLevel === selectedEntry?.accessLevel}
                                strong={true}
                                onClick={() => saveAccessLevel()}>
                                Save
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    { selectedEntry && <IonItem>
                        <IonLabel>
                            <h3>{ describeEntry(selectedEntry).user }</h3>
                            <p>{ describeEntry(selectedEntry).service }</p>
                        </IonLabel>
                    </IonItem> }
                    <IonItem>
                        <IonSelect label="Access level" value={editedAccessLevel}
                                   onIonChange={(ev) => setEditedAccessLevel(ev.detail.value)}>
                            {accessLevels.map(level =>
                                <IonSelectOption key={level.value} value={level.value}>{level.title}</IonSelectOption>)}
                        </IonSelect>
                    </IonItem>
                    <IonButton className="ion-margin-top" expand="block" color="danger" fill="outline" onClick={removeEntry}>
                        Remove
                    </IonButton>
                </IonContent>
            </IonModal>

            <IonModal ref={modal} trigger="open-access2access-modal" onWillDismiss={() => setNewEntry({})}>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton onClick={() => cancel()}>Cancel</IonButton>
                        </IonButtons>
                        <IonTitle>Add managed access</IonTitle>
                        <IonButtons slot="end">
                            <IonButton
                                disabled={!isValidId(newEntry.user) || !isValidId(newEntry.service) || newEntry.accessLevel === undefined}
                                strong={true}
                                onClick={() => confirm()}>
                                Confirm
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <Autocomplete
                        label="Managed user"
                        value={newEntry.user}
                        onSelect={(item) => setNewEntry(x => ({...x, user: item}))}
                        placeholder="Enter user name or id..."
                        fetchItems={getSearchableUsers}
                    />
                    <Autocomplete
                        label="Managed service"
                        value={newEntry.service}
                        onSelect={(item) => setNewEntry(x => ({...x, service: item}))}
                        placeholder="Enter service name or id..."
                        fetchItems={getSearchableServices}
                        formatValue={(item) => item.description ? `${item.title} · ${item.description}` : item.title}
                    />
                    <IonItem>
                        <IonSelect label="Access level" placeholder="Select access level" value={newEntry.accessLevel}
                                   onIonChange={(ev) => setNewEntry(x => ({...x, accessLevel: ev.detail.value}))}>
                            {accessLevels.map(level =>
                                <IonSelectOption key={level.value} value={level.value}>{level.title}</IonSelectOption>)}
                        </IonSelect>
                    </IonItem>
                </IonContent>
            </IonModal>
        </div>
    );
}
